// Tamil-Katral-Maiyam\src\pages\LandingPage.tsx

// ✅ COMMENTED: Language switch disabled (English only for now)
// import { LanguageSwitch } from "../components/ui/LanguageSwitch";

import React, { useMemo, useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  PenLine,
  BookMarked,
  Music,
  Shield,
  Lock,
  Eye,
  ChevronRight,
  GraduationCap,
  Target,
  Mic,
  TrendingUp,
  Menu,
  X,
  Moon,
  Sun,
} from "lucide-react";

import { Button } from "../components/ui/Button";
import { ROUTES } from "../utils/constants";
import { useTheme } from "../contexts/ThemeContext";
import { Section } from "../components/marketing/Section";
import { SplitFeature } from "../components/marketing/SplitFeature";
import { IconGrid, type IconGridItem } from "../components/marketing/IconGrid";
import { Tabs, type TabItem } from "../components/marketing/Tabs";
import { StepTimeline, type StepItem } from "../components/marketing/StepTimeline";
import { TestimonialSlider, type TestimonialItem } from "../components/marketing/TestimonialSlider";
import { MetricCard } from "../components/landing/MetricCard";

/* =========================
   English-only text (no t())
   ========================= */
const TEXT = {
  brandShort: "TKM",
  heroTitle: "Build connected learning journeys from the classroom to home.",
  heroSubtitle:
    "Admins, teachers, students, and parents — tools made for everyone in one secure platform.",

  login: "Log in",
  adminLogin: "Admin login",
  request: "Request access",

  nav: {
    how: "How it works",
    modules: "Modules",
    tracking: "Tracking",
    teachers: "Teachers",
    vocabulary: "Vocabulary",
    safety: "Safety",
  },
  days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  trust: {
    title: "Trusted by schools and families",
    item1: "Tamil Nadu Schools",
    item2: "CBSE Affiliates",
    item3: "International Tamil",
  },
  how: {
    title: "How it works",
    subtitle: "Four simple steps to start learning with the AI Tamil Learning Bot.",
    steps: [
      { step: 1, title: "Choose Grade", desc: "Select the learner’s grade so content and scenarios match their ability." },
      { step: 2, title: "Pick Scenario", desc: "Choose from role-play scenarios, stories, or creative prompts." },
      { step: 3, title: "Speak or Type", desc: "Learners respond by voice or text; the AI adapts and guides." },
      { step: 4, title: "Get Feedback & Progress", desc: "Real-time feedback and can-do tracking show mastery and vocabulary growth." },
    ],
  },
  coreModules: {
    title: "Core Learning Modules",
    subtitle: "Conversational learning, creative expression, stories, and rhymes.",
    cta: "Explore modules",
  },
  modulesGrid: [
    {
      title: "Conversational Learning",
      desc: "Role-play scenarios and multi-character dialogues.",
      bullets: ["Scenario-based role-play", "Multi-character dialogues", "Contextual Tamil practice"],
    },
    {
      title: "Creative Expression",
      desc: "Topic writing + voice + OCR.",
      bullets: ["Topic-based writing prompts", "Voice input support", "Structured feedback"],
    },
    {
      title: "Animated Stories",
      desc: "Age-appropriate narratives and listening.",
      bullets: ["Age-appropriate narratives", "Listening and comprehension", "Engaging characters"],
    },
    {
      title: "Animated Rhymes",
      desc: "Phonetic and rhythm practice.",
      bullets: ["Phonetic and rhythm practice", "Memorable patterns", "Fun repetition"],
    },
  ],
  analytics: {
    title: "Can-Do Tracking & Analytics",
    subtitle: "Sessions, mastery, and vocabulary growth at a glance.",
    body: "Track learner progress with sessions completed, can-do mastery, and vocabulary growth. Export reports for admins and parents.",
    metrics: {
      sessions: "Sessions",
      mastery: "Can-Do Mastery %",
      vocab: "Vocabulary growth",
      vocabSuffix: " words",
    },
  },
  teachers: {
    title: "Teacher Dashboard",
    subtitle: "Class summary, exportable reports, and content curation in one place.",
    bullets: [
      "Class-level overview and student progress",
      "Exportable reports for admins and parents",
      "Curate and assign content by grade and topic",
      "Monitor can-do achievement and vocabulary growth",
    ],
    cta: "Learn more for teachers",
  },
  vocabulary: {
    title: "Vocabulary & Dictionary + Games",
    subtitle: "Word of the day, synonyms, examples, and flashcards.",
    tabs: {
      dictionary: { label: "Dictionary", text: "Look up words with meanings, synonyms, antonyms, and example sentences in Tamil and English." },
      flashcards: { label: "Flashcards", text: "Quick drill with spaced repetition and audio support." },
      wordOfDay: { label: "Word of the day", text: "Daily word with pronunciation, usage, and related phrases." },
    },
  },
  testimonials: {
    title: "What educators say",
    subtitle: "Success stories from schools and families.",
    items: [
      { quote: "Our students love the conversational Tamil bot. Engagement has gone up significantly.", author: "Priya M.", role: "Teacher, Chennai" },
      { quote: "The can-do tracking helps us show ROI to leadership and progress to parents in a clear way.", author: "Raj K.", role: "School Admin" },
      { quote: "Finally a platform that feels safe and built for kids. COPPA matters to us.", author: "Lakshmi S.", role: "Parent" },
    ],
  },
  safety: {
    title: "Safety & Privacy",
    subtitle: "Designed for schools and families.",
    items: ["COPPA & GDPR compliant", "Data privacy built in", "Kid-safe design"],
    body: "We do not sell personal data. Schools and parents retain control over learner information.",
    privacyPolicy: "Privacy policy",
  },
  footer: {
    ctaTitle: "Ready to start?",
    ctaSubtitle: "Log in or request access for your school.",
    cols: {
      product: "Product",
      modules: "Modules",
      teachers: "Teachers",
      parents: "Parents",
      resources: "Resources",
      company: "Company",
    },
    links: {
      exploreModules: "Explore modules",
      teacherDashboard: "Teacher dashboard",
      privacy: "Privacy",
      signup: "Sign up",
      forgot: "Forgot password",
      contact: "Contact",
    },
    copyright: "TKM - Tamil Katral Maiyam. All rights reserved.",
  },
  common: {},
};

/* =========================
   SECTION 5 ChartMock (GLASS)
   - Background image visible
   - No solid white card
   - Bars are transparent (no blue)
   ========================= */
const ChartMock: React.FC<{ days: string[] }> = ({ days }) => {
  const bars: number[] = [38, 62, 44, 78, 54, 92, 68];

  return (
    <div
      className="
        rounded-[28px]
        border border-white/22
        bg-white/4
        backdrop-blur-0
        shadow-[0_14px_36px_rgba(0,0,0,0.18)]
        p-5 sm:p-6
      "
    >
      <div className="rounded-2xl border border-white/16 bg-transparent p-4 sm:p-6">
        <div className="h-[220px] sm:h-[260px] flex items-end justify-between gap-3">
          {bars.map((h: number, i: number) => (
            <div key={i} className="flex-1 h-full flex flex-col justify-end">
              {/* ✅ TRANSPARENT GLASS BAR */}
              <div
                className="
                  w-full rounded-[22px]
                  bg-white/6
                  border border-white/20
                  shadow-[0_10px_22px_rgba(0,0,0,0.10)]
                  backdrop-blur-[1px]
                  relative overflow-hidden
                "
                style={{ height: `${h}%` }}
              >
                {/* soft highlight */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/8 to-white/0" />
                {/* subtle sheen blobs */}
                <div className="absolute -left-6 top-6 h-20 w-20 rounded-full bg-white/10 blur-2xl" />
                <div className="absolute -right-10 top-10 h-28 w-28 rounded-full bg-white/8 blur-3xl" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between text-white font-bold drop-shadow-[0_3px_10px_rgba(0,0,0,0.75)]">
          {days.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [navOpen, setNavOpen] = useState(false);

  const scrollTo = useCallback((id: string) => {
    setNavOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const NAV_ANCHORS = useMemo(
    () => [
      { label: TEXT.nav.how, id: "how-it-works" },
      { label: TEXT.nav.modules, id: "modules" },
      { label: TEXT.nav.tracking, id: "analytics" },
      { label: TEXT.nav.teachers, id: "teachers" },
      { label: TEXT.nav.vocabulary, id: "vocabulary" },
      { label: TEXT.nav.safety, id: "safety" },
    ],
    []
  );

  const navLinkClass =
    "shrink-0 rounded-lg px-2.5 py-2 text-sm font-medium text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800 hover:text-app-text dark:hover:text-white transition-colors";

  const HOW_IT_WORKS_STEPS: StepItem[] = useMemo(
    () => [
      { step: 1, title: TEXT.how.steps[0].title, description: TEXT.how.steps[0].desc, icon: GraduationCap },
      { step: 2, title: TEXT.how.steps[1].title, description: TEXT.how.steps[1].desc, icon: Target },
      { step: 3, title: TEXT.how.steps[2].title, description: TEXT.how.steps[2].desc, icon: Mic },
      { step: 4, title: TEXT.how.steps[3].title, description: TEXT.how.steps[3].desc, icon: TrendingUp },
    ],
    []
  );

  const MODULES_GRID: IconGridItem[] = useMemo(
    () => [
      {
        icon: MessageSquare,
        title: TEXT.modulesGrid[0].title,
        description: TEXT.modulesGrid[0].desc,
        bullets: TEXT.modulesGrid[0].bullets,
      },
      {
        icon: PenLine,
        title: TEXT.modulesGrid[1].title,
        description: TEXT.modulesGrid[1].desc,
        bullets: TEXT.modulesGrid[1].bullets,
      },
      {
        icon: BookMarked,
        title: TEXT.modulesGrid[2].title,
        description: TEXT.modulesGrid[2].desc,
        bullets: TEXT.modulesGrid[2].bullets,
      },
      {
        icon: Music,
        title: TEXT.modulesGrid[3].title,
        description: TEXT.modulesGrid[3].desc,
        bullets: TEXT.modulesGrid[3].bullets,
      },
    ],
    []
  );

  const VOCAB_TABS: TabItem[] = useMemo(
    () => [
      {
        id: "dictionary",
        label: TEXT.vocabulary.tabs.dictionary.label,
        content: <p className="text-app-text-muted dark:text-gray-400">{TEXT.vocabulary.tabs.dictionary.text}</p>,
      },
      {
        id: "flashcards",
        label: TEXT.vocabulary.tabs.flashcards.label,
        content: <p className="text-app-text-muted dark:text-gray-400">{TEXT.vocabulary.tabs.flashcards.text}</p>,
      },
      {
        id: "word-of-day",
        label: TEXT.vocabulary.tabs.wordOfDay.label,
        content: <p className="text-app-text-muted dark:text-gray-400">{TEXT.vocabulary.tabs.wordOfDay.text}</p>,
      },
    ],
    []
  );

  const TESTIMONIALS: TestimonialItem[] = useMemo(
    () => [
      { quote: TEXT.testimonials.items[0].quote, author: TEXT.testimonials.items[0].author, role: TEXT.testimonials.items[0].role },
      { quote: TEXT.testimonials.items[1].quote, author: TEXT.testimonials.items[1].author, role: TEXT.testimonials.items[1].role },
      { quote: TEXT.testimonials.items[2].quote, author: TEXT.testimonials.items[2].author, role: TEXT.testimonials.items[2].role },
    ],
    []
  );

  const CHART_DAYS = useMemo(() => TEXT.days, []);

  return (
    <div id="top" className="min-h-screen text-app-text dark:text-gray-100">
      <nav className="sticky top-0 z-50 border-b border-app-border dark:border-gray-800 bg-app-surface/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-app-surface/80 dark:supports-[backdrop-filter]:bg-gray-900/80">
        <div className="mx-auto grid max-w-6xl grid-cols-[auto_1fr_auto] items-center gap-4 px-4 h-[72px] sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => scrollTo("top")}
            className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors"
            aria-label="TKM Home"
          >
            <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl overflow-hidden bg-white ring-1 ring-black/10 shadow-[0_10px_24px_rgba(0,0,0,0.18)]">
              <img src="/images/tkm-logo.png" alt="TKM Logo" className="h-full w-full object-contain p-1" draggable={false} />
            </span>
            <div className="hidden md:block leading-tight text-left">
              <div className="text-base font-extrabold text-app-text dark:text-white">Tamil Katral Maiyam</div>
            </div>
          </button>

          <div className="hidden lg:flex items-center justify-center gap-1 overflow-x-auto">
            {NAV_ANCHORS.map((item) => (
              <button key={item.id} type="button" onClick={() => scrollTo(item.id)} className={navLinkClass}>
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => navigate(`${ROUTES.LOGIN}?mode=admin`)}>
              {TEXT.adminLogin}
            </Button>
            <Button size="sm" className="hidden sm:inline-flex" onClick={() => navigate(ROUTES.REGISTER)}>
              {TEXT.request}
            </Button>
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="lg:hidden p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      {navOpen ? (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />
          <div className="fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] bg-app-surface dark:bg-gray-900 border-l border-app-border dark:border-gray-800 shadow-xl lg:hidden flex flex-col py-6 px-4 overflow-y-auto">
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={toggleTheme} className="p-2 rounded-lg" aria-label="Toggle theme">
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <button type="button" onClick={() => setNavOpen(false)} className="p-2 rounded-lg" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col gap-1 mt-4">
              {NAV_ANCHORS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="text-left px-4 py-3 rounded-lg font-medium hover:bg-app-surface-muted dark:hover:bg-gray-800"
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-app-border/20 dark:border-gray-800/60 space-y-2">
              <Button className="w-full" variant="outline" onClick={() => { setNavOpen(false); navigate(`${ROUTES.LOGIN}?mode=admin`); }}>
                {TEXT.adminLogin}
              </Button>
              <Button className="w-full" onClick={() => { setNavOpen(false); navigate(ROUTES.REGISTER); }}>
                {TEXT.request}
              </Button>
            </div>
          </div>
        </>
      ) : null}

       {/* Section 1: Hero — FULL cover background */}
<section className="relative min-h-screen w-full overflow-hidden flex items-center text-white px-4 sm:px-6 lg:px-8">
  {/* ✅ Full background (covers entire section) */}
  <div
    className="absolute inset-0 bg-no-repeat bg-center bg-cover"
    style={{
      backgroundImage: `url("/images/tkm-hero.jpg.jpeg")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  />

  {/* ✅ Overlay for readability */}
  <div className="absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-900/45 to-slate-900/10" />

  {/* ✅ Content */}
  <div className="relative mx-auto w-full max-w-6xl py-16 sm:py-20">
    <header className="space-y-4 max-w-xl sm:max-w-2xl">
      <p className="text-sm uppercase tracking-[0.3em] text-indigo-100/90">
        Tamil Katral Maiyam
      </p>

      <h1 className="text-4xl font-semibold leading-tight tracking-wide sm:text-5xl lg:text-6xl drop-shadow-[0_6px_18px_rgba(0,0,0,0.45)]">
        {TEXT.heroTitle}
      </h1>

      <p className="text-lg text-white/85 sm:text-xl max-w-xl drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]">
        {TEXT.heroSubtitle}
      </p>

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <Button
          size="lg"
          className="font-extrabold shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
          onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
        >
          {TEXT.login}
        </Button>

        <Button
          variant="outline"
          size="lg"
          className="border-white/70 text-white hover:bg-white/10"
          onClick={() => navigate(ROUTES.REGISTER)}
        >
          {TEXT.request}
        </Button>
      </div>
    </header>
  </div>
</section>

      {/* Section 2: Trust / Logo bar — full image background + single highlight box */}
      <section className="relative py-10 sm:py-12">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: "url(/images/tkm_land_section2.jpeg)" }}
        />
        <div className="absolute inset-0 bg-black/10" />

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div
            className="
              w-full rounded-[42px]
              px-6 sm:px-12 py-8
              bg-white/80
              backdrop-blur-none
              shadow-[0_18px_40px_rgba(0,0,0,0.22)]
              border border-white/40
            "
          >
            <p
              className="
                text-center
                text-sm sm:text-[13px]
                font-black
                uppercase
                tracking-[0.28em]
                text-slate-800
              "
            >
              {TEXT.trust.title}
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-8 sm:gap-14">
              {[TEXT.trust.item1, TEXT.trust.item2, TEXT.trust.item3].map((name) => (
                <span
                  key={name}
                  className="
                    text-sm sm:text-[15px]
                    font-extrabold
                    tracking-wide
                    text-slate-700
                    drop-shadow-[0_2px_8px_rgba(0,0,0,0.08)]
                  "
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: How it works — equal height cards */}
<Section
  id="how-it-works"
  title={TEXT.how.title}
  subtitle={TEXT.how.subtitle}
  variant="smoothSolid"
  bgImage="/images/tkm_land_section3.jpeg"
  overlay="dark"
>
  {/* ✅ force NO drop-shadow inside this section */}
  <div
    className={[
      "[&_*]:drop-shadow-none",
      // ✅ make row/grid stretch
      "[&_.tkm-steps-grid]:items-stretch",
      // ✅ make each step wrapper full height
      "[&_.tkm-step-item]:h-full",
      // ✅ force card min-height so all same
      "[&_.tkm-step-card]:min-h-[220px]",
      "[&_.tkm-step-card]:h-full",
      // ✅ center content nicely
      "[&_.tkm-step-card]:flex",
      "[&_.tkm-step-card]:flex-col",
      "[&_.tkm-step-card]:justify-center",
    ].join(" ")}
  >
    <StepTimeline steps={HOW_IT_WORKS_STEPS} />
  </div>
</Section>


      {/* Section 4: Core Learning Modules — image bg + white highlighted text (ALL BOLD) (NO black shadow) */}
      <Section
        id="modules"
        title={TEXT.coreModules.title}
        subtitle={TEXT.coreModules.subtitle}
        variant="smoothSolid"
        bgImage="/images/tkm_land_section4.jpeg"
        overlay="dark"
      >
        <div
          className="
            rounded-[28px]
            border border-white/20
            bg-black/25
            backdrop-blur-md
            shadow-[0_18px_40px_rgba(0,0,0,0.35)]
            p-4 sm:p-6
            text-white
            [&_*]:text-white
            [&_*]:font-bold
            [&_h3]:font-extrabold
            [&_h4]:font-extrabold
            [&_p]:text-white/95
            [&_li]:text-white/95
            [&_*]:drop-shadow-none
          "
        >
          <IconGrid items={MODULES_GRID} columns={4} />
        </div>

        <div className="mt-12 flex justify-center">
          <Button size="lg" className="shadow-[0_18px_40px_rgba(0,0,0,0.35)]" onClick={() => navigate(ROUTES.MODULES)}>
            {TEXT.coreModules.cta}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </Section>

{/* Section 5: Can-Do Tracking + Analytics — image bg (LIGHT + DARK readable) */}
<Section
  id="analytics"
  title={TEXT.analytics.title}
  subtitle={TEXT.analytics.subtitle}
  variant="smoothSolid"
  bgImage="/images/tkm_land_section5.jpeg"
  overlay="none"
>
  <SplitFeature
    visual={<div className="hidden" aria-hidden="true" />}
    reverse={false}
    visualSlide="right"
  >
    {/* ✅ Glass container (background image visible like testimonial) */}
    <div
      className="
        rounded-[28px]
        border border-white/20
        bg-white/16 dark:bg-black/18
        backdrop-blur-sm
        shadow-[0_18px_40px_rgba(0,0,0,0.22)]
        p-5 sm:p-6

        text-slate-900 dark:text-white
        font-bold

        [&_h3]:font-extrabold
        [&_h4]:font-extrabold
        [&_p]:text-slate-900/95 dark:[&_p]:text-white/95
        [&_*]:drop-shadow-none
      "
    >
      <div className="space-y-6">
        <p className="text-slate-900/95 dark:text-white/95">
          {TEXT.analytics.body}
        </p>

        {/* ✅ Equal height + aligned cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
          {/* CARD 1 */}
          <div className="h-full">
            <MetricCard
              label={TEXT.analytics.metrics.sessions}
              value={124}
              transparent
            />
          </div>

          {/* CARD 2 */}
          <div className="h-full">
            <MetricCard
              label={TEXT.analytics.metrics.mastery}
              value={87}
              suffix="%"
              transparent
            />
          </div>

          {/* CARD 3 */}
          <div className="h-full">
            <MetricCard
              label={TEXT.analytics.metrics.vocab}
              value={420}
              suffix={TEXT.analytics.metrics.vocabSuffix}
              transparent
            />
          </div>
        </div>
      </div>
    </div>
  </SplitFeature>
</Section>



     {/* Section 6: Teacher Dashboard — image bg (NO black shadow) */}
<Section
  id="teachers"
  title={TEXT.teachers.title}
  subtitle={TEXT.teachers.subtitle}
  variant="smoothSolid"
  bgImage="/images/tkm_land_section6.jpeg.jpeg"
  overlay="dark"
>
  <SplitFeature
  visual={<div className="hidden" aria-hidden="true" />}
  reverse={false}
  visualSlide="right"
>
  <div
    className="
      rounded-[28px]
      border border-white/20
      bg-black/25
      backdrop-blur-md
      shadow-[0_18px_40px_rgba(0,0,0,0.35)]
      p-5 sm:p-6
      text-white
      [&_*]:text-white
      [&_*]:font-bold
      [&_*]:drop-shadow-none
    "
  >
    <ul className="space-y-4">
      {TEXT.teachers.bullets.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-white/85" />
          {item}
        </li>
      ))}
    </ul>

    <Button className="mt-6" onClick={() => navigate(ROUTES.TEACHER_LANDING)}>
      {TEXT.teachers.cta}
    </Button>
  </div>
</SplitFeature>

</Section>

      {/* Section 7: Vocabulary + Dictionary + Games — transparent UI, ALL tab buttons highlighted */}
      <Section
        id="vocabulary"
        title={TEXT.vocabulary.title}
        subtitle={TEXT.vocabulary.subtitle}
        variant="smoothSolid"
        bgImage="/images/tkm_land_section7.jpeg.jpeg"
        overlay="dark"
      >
        {/* ✅ No big box. Only text has shadow + Tabs are transparent */}
        <div
          className="
            mx-auto max-w-5xl px-4 sm:px-0
            text-white
            [&_*]:text-white
            [&_*]:font-bold
            [&_*]:drop-shadow-[0_4px_14px_rgba(0,0,0,0.75)]

            /* ✅ highlight ALL tab buttons */
            [&_button]:bg-white/14
            [&_button]:border
            [&_button]:border-white/28
            [&_button]:shadow-[0_10px_22px_rgba(0,0,0,0.18)]
            [&_button:hover]:bg-white/20
            [&_button]:backdrop-blur-sm
            [&_button]:rounded-xl
          "
        >
          {/* ✅ Force Tabs internal white cards to be transparent */}
          <div className="[&_*]:bg-transparent [&_*]:shadow-none [&_*]:border-white/20">
            <Tabs tabs={VOCAB_TABS} variant="pill" />
          </div>
        </div>
      </Section>

      {/* Section 8: What educators say — image bg (NO shadow) */}
      <Section
        id="testimonials"
        title={TEXT.testimonials.title}
        subtitle={TEXT.testimonials.subtitle}
        variant="smoothSolid"
        bgImage="/images/tkm_land_section8.jpeg.jpeg"
        overlay="dark"
      >
        {/* ✅ no text shadow */}
        <div className="mx-auto max-w-3xl text-white [&_*]:text-white [&_*]:font-bold">
          {/* keep slider transparent */}
          <div className="[&_*]:bg-transparent [&_*]:shadow-none [&_*]:border-white/20">
            <TestimonialSlider items={TESTIMONIALS} className="max-w-3xl mx-auto" />
          </div>
        </div>
      </Section>

      {/* Section 9: Safety & Privacy — image bg (NO shadow) */}
      <Section
        id="safety"
        title={TEXT.safety.title}
        subtitle={TEXT.safety.subtitle}
        variant="smoothSolid"
        bgImage="/images/tkm_land_section9.jpeg"
        overlay="dark"
      >
        {/* ✅ no text shadow */}
        <div className="max-w-3xl mx-auto text-white [&_*]:text-white [&_*]:font-bold">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            {[
              { icon: Shield, label: TEXT.safety.items[0] },
              { icon: Lock, label: TEXT.safety.items[1] },
              { icon: Eye, label: TEXT.safety.items[2] },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-2 text-center">
                {/* glass icon badge */}
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-white/10 backdrop-blur-sm">
                  <Icon className="h-6 w-6 opacity-95" aria-hidden />
                </div>
                <span className="text-sm">{label}</span>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-white/90">{TEXT.safety.body}</p>

          <p className="mt-4 text-center">
            <Link to={ROUTES.PRIVACY} className="text-sm font-bold text-white/95 hover:text-white hover:underline">
              {TEXT.safety.privacyPolicy}
            </Link>
          </p>
        </div>
      </Section>

      {/* Section 10: Footer - Full background image cover, white text bold, no black shadow */}
      <footer className="relative overflow-hidden text-white">
        {/* ✅ Background image */}
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: "url(/images/tkm_land_section10.jpeg)" }}
        />

        {/* ✅ Slight overlay so white text is readable (NO shadow) */}
        <div className="absolute inset-0 bg-black/30" />

        {/* ✅ Content */}
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
          {/* Top CTA row */}
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{TEXT.footer.ctaTitle}</h2>
              <p className="mt-2 text-base sm:text-lg font-bold text-white/95">{TEXT.footer.ctaSubtitle}</p>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" className="font-extrabold" onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}>
                {TEXT.login}
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="border-white/80 text-white font-extrabold hover:bg-white/10"
                onClick={() => navigate(ROUTES.REGISTER)}
              >
                {TEXT.request}
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/30 pt-10" />

          {/* Footer columns */}
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-6 pt-10">
            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.22em] text-white">{TEXT.footer.cols.product}</h3>
              <ul className="mt-4 space-y-3 text-[15px] font-bold text-white/95">
                <li><button type="button" onClick={() => scrollTo("how-it-works")} className="hover:text-white">{TEXT.nav.how}</button></li>
                <li><button type="button" onClick={() => scrollTo("modules")} className="hover:text-white">{TEXT.nav.modules}</button></li>
                <li><button type="button" onClick={() => scrollTo("analytics")} className="hover:text-white">{TEXT.nav.tracking}</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.22em] text-white">{TEXT.footer.cols.modules}</h3>
              <ul className="mt-4 space-y-3 text-[15px] font-bold text-white/95">
                <li><Link to={ROUTES.MODULES} className="hover:text-white">{TEXT.footer.links.exploreModules}</Link></li>
                <li><button type="button" onClick={() => scrollTo("vocabulary")} className="hover:text-white">{TEXT.nav.vocabulary}</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.22em] text-white">{TEXT.footer.cols.teachers}</h3>
              <ul className="mt-4 space-y-3 text-[15px] font-bold text-white/95">
                <li><button type="button" onClick={() => scrollTo("teachers")} className="hover:text-white">Teacher dashboard</button></li>
                <li><Link to={ROUTES.TEACHER_LANDING} className="hover:text-white">{TEXT.footer.links.teacherDashboard}</Link></li>
                <li><Link to={`${ROUTES.LOGIN}?mode=user`} className="hover:text-white">Teacher login</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.22em] text-white">{TEXT.footer.cols.parents}</h3>
              <ul className="mt-4 space-y-3 text-[15px] font-bold text-white/95">
                <li><button type="button" onClick={() => scrollTo("safety")} className="hover:text-white">{TEXT.safety.title}</button></li>
                <li><button type="button" onClick={() => scrollTo("testimonials")} className="hover:text-white">Testimonials</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.22em] text-white">{TEXT.footer.cols.resources}</h3>
              <ul className="mt-4 space-y-3 text-[15px] font-bold text-white/95">
                <li><Link to={ROUTES.PRIVACY} className="hover:text-white">{TEXT.footer.links.privacy}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.22em] text-white">{TEXT.footer.cols.company}</h3>
              <ul className="mt-4 space-y-3 text-[15px] font-bold text-white/95">
                <li><Link to={`${ROUTES.LOGIN}?mode=user`} className="hover:text-white">{TEXT.login}</Link></li>
                <li><Link to={ROUTES.REGISTER} className="hover:text-white">{TEXT.footer.links.signup}</Link></li>
                <li><Link to={ROUTES.FORGOT_PASSWORD} className="hover:text-white">{TEXT.footer.links.forgot}</Link></li>
                <li><a href="mailto:sales@tamilkatralmaiyam.org" className="hover:text-white">{TEXT.footer.links.contact}</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom divider + copyright */}
          <div className="mt-12 border-t border-white/25 pt-8">
            <p className="text-center text-sm sm:text-[15px] font-bold text-white/95">
              © {new Date().getFullYear()} {TEXT.footer.copyright}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};